import { type ChangeEvent, type InputHTMLAttributes, type JSX, type Ref, useId } from 'react';

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  readonly label: string;
  /** Already translated; the caller owns the i18n lookup. */
  readonly error?: string | undefined;
  readonly hint?: string | undefined;
  /**
   * What this field accepts as it is typed — one of `shared/lib/input`.
   *
   * The function runs on every keystroke and on paste, before `onChange` sees
   * the value, so the field and the form state can never disagree. It must be
   * pure and idempotent: `f(f(x)) === f(x)`.
   */
  readonly sanitize?: ((value: string) => string) | undefined;
  readonly ref?: Ref<HTMLInputElement>;
}

/**
 * The one text input. It always ships a visible label and an error slot: a red
 * border on its own is not a message (CLAUDE.md §7.5). min-h-11 = 44px.
 */
export function TextField({
  label,
  error,
  hint,
  id,
  className = '',
  sanitize,
  onChange,
  ref,
  ...rest
}: TextFieldProps): JSX.Element {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const messageId = `${inputId}-message`;
  const invalid = error !== undefined;

  /**
   * Rewrite the value in place, then let `onChange` see the clean one.
   *
   * Mutating `event.target.value` rather than only what we pass on is what
   * keeps this working under react-hook-form's `register`, which reads the
   * element itself. The caret is put back by hand: the browser drops it to the
   * end of a value it did not type, so a patient correcting the middle of their
   * name would be thrown to the end on every keystroke that removed a
   * character.
   */
  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (sanitize !== undefined) {
      const raw = event.target.value;
      const clean = sanitize(raw);

      if (clean !== raw) {
        // `selectionStart` is null on the input types that have no caret to
        // speak of (number, email, date) — there, the value alone is the point
        const caret = event.target.selectionStart;

        event.target.value = clean;

        if (caret !== null) {
          // how much of what was dropped sat before the caret
          const kept = sanitize(raw.slice(0, caret)).length;

          event.target.setSelectionRange(kept, kept);
        }
      }
    }

    onChange?.(event);
  };

  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      <label className="type-caption font-medium text-muted" htmlFor={inputId}>
        {label}
      </label>

      <input
        aria-describedby={invalid || hint !== undefined ? messageId : undefined}
        aria-invalid={invalid}
        className={[
          'min-h-11 w-full rounded-[var(--radius-md)] border bg-surface',
          'ps-3 pe-3 type-body text-text',
          'transition-colors duration-[var(--duration-instant)]',
          'placeholder:text-placeholder disabled:opacity-45',
          invalid ? 'border-danger' : 'border-border focus:border-primary',
        ].join(' ')}
        id={inputId}
        onChange={handleChange}
        ref={ref}
        {...rest}
      />

      {invalid ? (
        <p className="type-caption text-danger" id={messageId}>
          {error}
        </p>
      ) : hint === undefined ? null : (
        <p className="type-caption text-muted" id={messageId}>
          {hint}
        </p>
      )}
    </div>
  );
}
